import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/store/authStore';

type PasswordAuthStatus = 'allowed' | 'sent' | 'oauth' | 'not_found';
type OAuthProvider = 'google' | 'kakao' | null;

interface EmailAuthStatusResponse {
  status?: PasswordAuthStatus;
  provider?: OAuthProvider;
}

const PASSWORD_RECOVERY_REQUESTED_AT_KEY = 'shopee-manager-password-recovery-requested-at';
const PASSWORD_RECOVERY_ACTIVE_AT_KEY = 'shopee-manager-password-recovery-active-at';
const AUTH_REDIRECT_ORIGIN = 'https://sellitpapa.com';

const getAuthRedirectUrl = (path: string) => {
  return new URL(path, `${AUTH_REDIRECT_ORIGIN}/`).toString();
};

const normalizeAuthEmail = (email: string) => email.trim().toLowerCase();

const rememberPasswordRecovery = (key: string) => {
  try {
    window.localStorage.setItem(key, Date.now().toString());
  } catch (_) {
    // Storage can be unavailable in private browsing; the recovery link still carries auth data.
  }
};

export function useAuth() {
  const { user, session, loading, setUser, setSession, setLoading, signOut: storeSignOut } = useAuthStore();

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'PASSWORD_RECOVERY' && session) {
          rememberPasswordRecovery(PASSWORD_RECOVERY_ACTIVE_AT_KEY);
        }

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [setUser, setSession, setLoading]);

  const signUp = async (email: string, password: string) => {
    const normalizedEmail = normalizeAuthEmail(email);
    const redirectUrl = getAuthRedirectUrl('/');
    
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    const normalizedEmail = normalizeAuthEmail(email);

    const { error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password
    });
    return { error };
  };

  const getEmailAuthStatus = async (email: string) => {
    const normalizedEmail = normalizeAuthEmail(email);

    const { data, error } = await supabase.functions.invoke('request-password-reset', {
      body: {
        email: normalizedEmail
      }
    });

    return { data: data as EmailAuthStatusResponse | null, error };
  };

  const requestPasswordReset = async (email: string) => {
    const normalizedEmail = normalizeAuthEmail(email);
    const redirectTo = getAuthRedirectUrl('/reset-password?type=recovery');
    const { data, error } = await getEmailAuthStatus(normalizedEmail);

    if (error || data?.status !== 'allowed') {
      return { data, error };
    }

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo
    });

    if (!resetError) {
      rememberPasswordRecovery(PASSWORD_RECOVERY_REQUESTED_AT_KEY);
    }

    return { data: { status: 'sent' }, error: resetError };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getAuthRedirectUrl('/auth?from=oauth')
      }
    });
    return { error };
  };

  const signInWithKakao = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'kakao',
      options: {
        redirectTo: getAuthRedirectUrl('/auth?from=oauth'),
        scopes: 'profile_nickname account_email',
        queryParams: { lang: 'ko' }
      }
    });
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      storeSignOut();
    }
    return { error };
  };

  return {
    user,
    session,
    loading,
    signUp,
    signIn,
    getEmailAuthStatus,
    requestPasswordReset,
    updatePassword,
    signInWithGoogle,
    signInWithKakao,
    signOut
  };
}
